import { useId } from 'react';

/**
 * One titled card inside an admin or settings panel.
 *
 * It exists so that every section on both routes gets the same heading level,
 * the same `aria-labelledby` wiring and the same description slot — the
 * pre-Etch admin components each rolled their own header markup, which is how
 * the heading order drifted (spec §12 requires a logical hierarchy).
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.title
 * @param {import('react').ReactNode} [props.description]
 * @param {import('react').ReactNode} [props.actions] - right-aligned controls.
 * @param {'h2'|'h3'} [props.as='h2'] - heading level for the title.
 * @param {'danger'} [props.tone]
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.className]
 */
export default function SettingsGroup({
  title,
  description,
  actions,
  as: Heading = 'h2',
  tone,
  children,
  className = '',
  ...rest
}) {
  const headingId = useId();

  return (
    <section
      className={`etch-settings-group ${className}`.trim()}
      data-tone={tone}
      aria-labelledby={headingId}
      {...rest}
    >
      <header className="etch-settings-group__header">
        <div className="etch-settings-group__heading">
          <Heading id={headingId} className="etch-settings-group__title">
            {title}
          </Heading>
          {description && <p className="etch-settings-group__desc">{description}</p>}
        </div>
        {actions && <div className="etch-settings-group__actions">{actions}</div>}
      </header>
      <div className="etch-settings-group__body">{children}</div>
    </section>
  );
}
